import { IDynamicTable, NetworkResponse } from "~/app/type/common";
import { requestREST } from "~/client/http/CommonHTTP";
import { CourseLevel } from "~/entity/Courses/model/shema";
import {
  EmployeeCard,
  EmployeeCardUpdateParams,
  EmployeeCreateParams,
  EmployeeDocumentType,
  EmployeeDocumentUploadResponse,
  EmployeeEducationDealerReportGetterParams,
  EmployeeEducationGetterParams,
  EmployeeEducationProgram,
  EmployeeEducationProgramWithCourses,
  EmployeeEducationSetterParams,
  EmployeeEducationStatisticsCoursesDetailsReportGetterParams,
  EmployeeEducationStatisticsCoursesReportGetterParams,
  EmployeeEmptyListParams,
  EmployeeEmptyListResponse,
  EmployeeExpectationsGetterParams,
  EmployeeExpectationsResponse,
  EmployeeFireParams,
  EmployeeFirePositionsParams,
  EmployeeFirePositionsResponse,
  EmployeeRestoreParams,
  EmployeesListParams,
  EmployeesListResponse,
  EmployeeStatisticsReportGetterParams,
  EmployeeStatusChangeParams,
} from "~/entity/Employee/model/schema";
import { paramsSerializer } from "~/shared/lib/paramsSerializer";

const employeeServiceMap = {
  list: "/employee",
  empty: "/employee/empty",
  expectations: "/employee/expectations",
  getStatistics: "/employee/statistics",
  getDealerLevelReport: "/employee/education/employee/dealer/level/report",
  getCourseByExpectation: "/employee/reports/course-by-expectation",
  getCoursesByExpectation: "/employee/reports/courses-by-expectation",
  getEmployeeCoursesRequired: "/employee/reports/employees-no-course",
  getStatisticsCoursesReport: "/employee/statistics/report/courses",
  getEducationPrograms: "/employee/education-programs",
  card: "/employee/card",
  firePositions: "/employee/card/fire-positions",
  uploadEmployeeDocument: "/employee/documents/upload",
};

export class EmployeeService {
  static GET = {
    list: (
      params: EmployeesListParams,
    ): NetworkResponse<EmployeesListResponse> =>
      requestREST.get(employeeServiceMap.list, {
        params,
        paramsSerializer: (query) =>
          paramsSerializer(query, { skipNulls: true }),
      }),

    empty: (
      params: EmployeeEmptyListParams,
    ): NetworkResponse<EmployeeEmptyListResponse> =>
      requestREST.get(employeeServiceMap.empty, {
        params,
        paramsSerializer: (query) =>
          paramsSerializer(query, { skipNulls: true }),
      }),

    expectations: (
      params?: EmployeeExpectationsGetterParams,
    ): NetworkResponse<EmployeeExpectationsResponse> =>
      requestREST.get(employeeServiceMap.expectations, { params }),

    getDealerLevelReport: (
      params: EmployeeEducationDealerReportGetterParams,
    ): NetworkResponse<{ fileHash: string }> => {
      return requestREST.get(employeeServiceMap.getDealerLevelReport, {
        params,
      });
    },
    getEmployeeCoursesRequired: (
      params: EmployeeEducationStatisticsCoursesDetailsReportGetterParams,
    ): NetworkResponse<{ table: IDynamicTable }> => {
      return requestREST.get(employeeServiceMap.getEmployeeCoursesRequired, {
        params,
      });
    },
    getCourseByExpectation: (
      params: EmployeeEducationStatisticsCoursesDetailsReportGetterParams,
    ): NetworkResponse<{ table: IDynamicTable }> => {
      return requestREST.get(employeeServiceMap.getCourseByExpectation, {
        params,
      });
    },
    getCoursesByExpectation: (
      params: EmployeeEducationStatisticsCoursesDetailsReportGetterParams,
    ): NetworkResponse<{ table: IDynamicTable }> => {
      return requestREST.get(employeeServiceMap.getCoursesByExpectation, {
        params,
      });
    },
    getStatistics: (
      params: EmployeeStatisticsReportGetterParams,
    ): NetworkResponse<{ fileHash: string }> => {
      return requestREST.get(employeeServiceMap.getStatistics, {
        params,
      });
    },
    getStatisticsCoursesReport: (
      params: EmployeeEducationStatisticsCoursesReportGetterParams,
    ): NetworkResponse<{ table: IDynamicTable }> => {
      return requestREST.get(employeeServiceMap.getStatisticsCoursesReport, {
        params,
      });
    },
    getEducationPrograms: (
      params: EmployeeEducationGetterParams,
    ): NetworkResponse<EmployeeEducationProgram> => {
      return requestREST.get(employeeServiceMap.getEducationPrograms, {
        params,
      });
    },
    getEducationProgramsById: (
      id: string,
      params: EmployeeEducationGetterParams,
    ): NetworkResponse<{
      program: EmployeeEducationProgramWithCourses;
      levels: CourseLevel[];
    }> => {
      return requestREST.get(
        employeeServiceMap.getEducationPrograms + `/${id}`,
        {
          params,
        },
      );
    },
    card: (id: string): NetworkResponse<EmployeeCard> => {
      return requestREST.get(`${employeeServiceMap.card}/${id}`);
    },
  };

  static PUT = {
    updateEducationProgram: (
      params: EmployeeEducationSetterParams,
    ): NetworkResponse<undefined> => {
      return requestREST.put(employeeServiceMap.getEducationPrograms, params);
    },
    updateCard: (
      id: string,
      data: EmployeeCardUpdateParams,
    ): NetworkResponse<{ success: boolean }> => {
      return requestREST.put(`${employeeServiceMap.card}/${id}`, data);
    },
    changeStatus: (
      id: string,
      data: EmployeeStatusChangeParams,
    ): NetworkResponse<{ success: boolean }> => {
      return requestREST.put(`${employeeServiceMap.list}/${id}/status`, data);
    },
    verifyStatus: (
      id: string,
      positionId: string,
    ): NetworkResponse<{ success: boolean }> => {
      return requestREST.put(
        `${employeeServiceMap.list}/${id}/status/verify`,
        undefined,
        {
          params: { positionId },
        },
      );
    },
    rejectStatus: (
      id: string,
      positionId: string,
      comment: string,
    ): NetworkResponse<{ success: boolean }> => {
      return requestREST.put(
        `${employeeServiceMap.list}/${id}/status/reject`,
        { comment },
        { params: { positionId } },
      );
    },
    restore: (
      id: string,
      data: EmployeeRestoreParams,
    ): NetworkResponse<{ success: boolean }> => {
      return requestREST.put(`${employeeServiceMap.list}/${id}/restore`, data);
    },
  };

  static DELETE = {
    fireCard: (
      id: string,
      data: EmployeeFireParams,
    ): NetworkResponse<{ success: boolean }> => {
      return requestREST.delete(`${employeeServiceMap.card}/${id}`, { data });
    },
  };

  static POST = {
    getEmployeeReport: (
      data: Record<string, unknown>,
    ): NetworkResponse<{ fileHash: string }> =>
      requestREST.post("/employee/report", data),
    getStatisticsCoursesReportFileHash: (
      params: EmployeeEducationStatisticsCoursesReportGetterParams,
    ): NetworkResponse<{ fileHash: string }> => {
      return requestREST.post(
        employeeServiceMap.getStatisticsCoursesReport,
        params,
      );
    },
    getCourseByExpectationFileHash: (
      data: EmployeeEducationStatisticsCoursesReportGetterParams,
    ): NetworkResponse<{ fileHash: string }> => {
      return requestREST.post(employeeServiceMap.getCourseByExpectation, data);
    },
    getCoursesByExpectationFileHash: (
      data: EmployeeEducationStatisticsCoursesReportGetterParams,
    ): NetworkResponse<{ fileHash: string }> => {
      return requestREST.post(employeeServiceMap.getCoursesByExpectation, data);
    },
    getEmployeeCoursesRequiredFileHash: (
      data: EmployeeEducationStatisticsCoursesReportGetterParams,
    ): NetworkResponse<{ fileHash: string }> => {
      return requestREST.post(
        employeeServiceMap.getEmployeeCoursesRequired,
        data,
      );
    },
    setEducationPrograms: (
      data: Omit<EmployeeEducationSetterParams, "instanceId">,
    ): NetworkResponse<{ instanceId: string }> => {
      return requestREST.post(employeeServiceMap.getEducationPrograms, data);
    },
    create: (data: EmployeeCreateParams): NetworkResponse<{ id: number }> => {
      return requestREST.post(employeeServiceMap.list, data);
    },
    firePositions: (
      data: EmployeeFirePositionsParams,
    ): NetworkResponse<EmployeeFirePositionsResponse> =>
      requestREST.post(employeeServiceMap.firePositions, data),
    sendCheck: (id: string): NetworkResponse<{ success: boolean }> => {
      return requestREST.post(
        `${employeeServiceMap.list}/${id}/status/send-check`,
      );
    },
    uploadEmployeeDocument: (
      file: File,
      type: EmployeeDocumentType,
      employeePositionId: string,
    ): NetworkResponse<EmployeeDocumentUploadResponse> => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", type);
      formData.append("employeePositionId", employeePositionId);
      return requestREST.post(
        employeeServiceMap.uploadEmployeeDocument,
        formData,
      );
    },
  };
}
